import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

const COMMIT_DEBOUNCE_MS = 250;

/**
 * Local draft state for a controlled primitive input whose commits are debounced.
 *
 * The synchronization ref prevents an upstream paste, reset, or hydration
 * update from being overwritten by a stale local draft.
 */
export const useDebouncedDraft = <T extends string | number>(
  value: T | undefined,
  onCommit: (next: T | '') => void,
  delay: number = COMMIT_DEBOUNCE_MS,
) => {
  const upstreamValue = (value ?? '') as T | '';
  const [draft, setDraft] = useState(upstreamValue);
  const [debouncedDraft] = useDebouncedValue(draft, delay);

  const lastSyncedRef = useRef(upstreamValue);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (upstreamValue === lastSyncedRef.current) return;
    lastSyncedRef.current = upstreamValue;
    setDraft(upstreamValue);
  }, [upstreamValue]);

  useEffect(() => {
    if (debouncedDraft === lastSyncedRef.current) return;
    lastSyncedRef.current = debouncedDraft;
    onCommitRef.current(debouncedDraft);
  }, [debouncedDraft]);

  return [draft, setDraft] as const;
};
